import React, { useEffect, useMemo, useState, useRef } from 'react';
import { observer } from 'mobx-react';
import { Field, Formik, FormikProps } from 'formik';
import _ from 'lodash';
import { AddressType, CountryType, RelationshipType, SexType, StateType, SupportPayer, SupportSubscriber, CreateClaimSubscriber, ProviderPayer } from '../../../../api';
import { getOptions } from '../../../base/select';
import * as S from '../form.styles';
import { ClaimEditorStore, DISABLED_PAYER_MESSAGE } from '../../../../stores/claim-editor';
import { InputField, SelectField } from '../../../base/form';
import { AsyncSelectField } from '../../../base/form/async-select/async-select';
import { DatePickerField } from '../../../base/form/datepicker/date-picker';
import { validateSubscriberFormValues } from '../../../../stores/claim-editor/claim-editor-rules';
import { fieldDomId } from '../../../../stores/claim-editor/claim-editor-sections';
import { formatLookupInfo } from '../../../../stores/claim-editor/claim-editor-lookup-info';
import { useClaimEditorRevealFieldErrors } from '../use-claim-editor-form-validation';
import { searchPayers } from '../../../../services/payer-search/payer-search';
import {
    subscriberIdentityKey,
    subscriberIdentityFromRecord,
} from '../../../../stores/claim-editor/claim-editor-subscriber-identity';
import { useVerifiedLink } from '../use-verified-link';
import { FormErrorBanner } from '../../../base/form/form-error-banner';

interface SubscriberPayerFormValues {
    subscriberLookup?: string;
    payerLookup?: string;
    payerId?: string;
    memberId?: string;
    ssn?: string;
    first: string;
    middle?: string;
    last: string;
    suffix?: string;
    dob?: string;
    sex?: SexType | '';
    phone?: string;
    relationshipToPatient?: RelationshipType | '';
    address1: string;
    address2?: string;
    city: string;
    state?: StateType | '';
    country?: CountryType | '';
    postal: string;
}

/**
 * How many payers the lookup will render at once.
 *
 * 50, matching `billing-provider-form` in this same editor — the payer lookup was
 * the outlier at 10 while its siblings used 20 and 50, on the field with by far
 * the largest list. react-select scrolls its own menu at this size, which is what
 * makes the results past the first handful reachable at all.
 */
const MAX_PAYER_OPTIONS = 50;

const emptyValues: SubscriberPayerFormValues = {
    subscriberLookup: '',
    payerLookup: '',
    payerId: '',
    memberId: '',
    ssn: '',
    first: '',
    middle: '',
    last: '',
    suffix: '',
    dob: '',
    sex: '' as any,
    phone: '',
    relationshipToPatient: '' as any,
    address1: '',
    address2: '',
    city: '',
    state: '' as any,
    country: CountryType.US as any,
    postal: '',
};

interface ISubscriberPayerFormProps {
    claimEditorStore: ClaimEditorStore;
    data?: CreateClaimSubscriber;
    subscriberIndex: number;
}

export const SubscriberPayerForm: React.FunctionComponent<ISubscriberPayerFormProps> = observer(({ claimEditorStore, data, subscriberIndex }) => {
    const validationSignal = claimEditorStore.validationSignal;
    const validationEvaluated = claimEditorStore.validationEvaluated;
    const sexOptions = getOptions(SexType);
    const stateOptions = getOptions(StateType);
    const countryOptions = getOptions(CountryType);
    const relationshipOptions = getOptions(RelationshipType);
    const formikRef = useRef<FormikProps<SubscriberPayerFormValues> | null>(null);
    const fieldPrefix = `subscribers[${subscriberIndex}]`;

    /**
     * The subscriber record `subscriberId` currently stands for.
     *
     * It is deliberately NOT a form field. The API ignores every other subscriber field
     * when `subscriberId` is present, so an id left behind by an earlier lookup made an
     * edited name inert: the form showed JUAN SANTANA while the claim transmitted the
     * subscriber the id pointed at. Holding the link out of band lets `convertToSubscriber`
     * drop the id the moment the form stops describing that person.
     *
     * The baseline is the CANONICAL record fetched by id, never the claim's own fields: a
     * claim already carrying one subscriber's id beside another's name would otherwise have
     * that pairing blessed and re-saved unchanged. Until the fetch answers — or if it fails —
     * no id is sent; see use-verified-link.
     */
    const { setLink, resolveLinked, isPending, settled, verificationFailed, retry } = useVerifiedLink(
        (data as any)?.subscriberId || (data as any)?.id || '',
        (id: string) => claimEditorStore.getSubscriberById(id),
        (record: any) => subscriberIdentityKey(subscriberIdentityFromRecord(record)),
    );

    const identityOf = (v: SubscriberPayerFormValues) => ({
        first: v.first,
        middle: v.middle,
        last: v.last,
        suffix: v.suffix,
        dob: v.dob,
        sex: v.sex,
        ssn: v.ssn,
        phone: v.phone,
        lines: [v.address1, v.address2],
        city: v.city,
        state: v.state,
        postal: v.postal,
        country: v.country,
    });

    const convertToSubscriber = (v: SubscriberPayerFormValues): CreateClaimSubscriber => {
        const lines = [v.address1].filter(Boolean);
        if (v.address2) lines.push(v.address2);
        return {
            subscriberId: resolveLinked(subscriberIdentityKey(identityOf(v))),
            first: v.first || '',
            middle: v.middle || undefined as any,
            last: v.last || '',
            suffix: v.suffix?.trim() ? v.suffix.trim() : undefined as any,
            dob: (v.dob as any) || undefined,
            sex: (v.sex as any) || undefined,
            phone: v.phone || undefined,
            relationshipToPatient: (v.relationshipToPatient as any) || undefined,
            memberId: v.memberId || '',
            ssn: v.ssn || undefined,
            payerId: v.payerId || '',
            addresses: [
                {
                    name: `${(v.first || '').trim()} ${(v.last || '').trim()}`.trim(),
                    lines,
                    city: v.city || '',
                    state: (v.state as any) || undefined,
                    postal: v.postal || '',
                    country: (v.country as any) || CountryType.US,
                    type: AddressType.MAILING,
                } as any,
            ],
        } as CreateClaimSubscriber;
    };

    const persistSubscriber = (values: SubscriberPayerFormValues): void => {
        // Until the id is verified this could only write the record back WITHOUT it; the
        // `settled` effect below persists again once the answer is known.
        if (isPending()) return;
        const updatedSubscriber = convertToSubscriber(values);
        const subscribers = [...(claimEditorStore.subscribers || [])];
        subscribers[subscriberIndex] = updatedSubscriber;
        claimEditorStore.updateSubscribers(subscribers);
    };

    const debouncedSave = useMemo(
        () =>
            _.debounce(() => {
                const latestValues = formikRef.current?.values;
                if (!latestValues) return;
                persistSubscriber(latestValues);
            }, 500),
        [claimEditorStore, subscriberIndex],
    );

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
        };
    }, [debouncedSave]);

    useEffect(() => {
        if (settled) debouncedSave();
    }, [settled, debouncedSave]);

    const initialValues = useMemo<SubscriberPayerFormValues>(() => {
        const s = data;
        if (!s) return emptyValues;
        const addr = s.addresses?.[0];
        return {
            subscriberLookup: s.last || '',
            payerLookup: s.payerId || '',
            payerId: s.payerId || '',
            memberId: s.memberId || '',
            ssn: s.ssn || '',
            first: s.first || '',
            middle: s.middle || '',
            last: s.last || '',
            suffix: s.suffix || '',
            dob: (s.dob as any) || '',
            sex: (s.sex as any) || '' as any,
            phone: s.phone || '',
            relationshipToPatient: (s as any).relationshipToPatient || '' as any,
            address1: addr?.lines?.[0] || '',
            address2: addr?.lines?.[1] || '',
            city: addr?.city || '',
            state: (addr?.state as any) || '' as any,
            country: (addr?.country as any) || CountryType.US as any,
            postal: addr?.postal || '',
        };
    }, [data]);

    return (
        <Formik<SubscriberPayerFormValues>
            innerRef={formikRef as any}
            initialValues={initialValues}
            validate={values =>
                validateSubscriberFormValues(values, {
                    isPayerIdActive: claimEditorStore.isPayerIdActive,
                })
            }
            onSubmit={() => {}}
            enableReinitialize
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={false}
        >
            {formik => {
                // Auto-save on value change - save whenever values change, not just when dirty
                useEffect(() => {
                    debouncedSave();
                }, [formik.values, debouncedSave]);

                useClaimEditorRevealFieldErrors(formik, validationSignal, validationEvaluated, () => {
                    debouncedSave.cancel();
                    persistSubscriber(formik.values);
                });

                return (
                <S.FormContainer>
                    {verificationFailed && (
                        <FormErrorBanner
                            messages={['Could not verify the linked subscriber record, so changes to the subscriber are not being saved.']}
                            onRetry={retry}
                        />
                    )}
                    <S.FormGrid>
                        <S.FormField span={2}>
                            <S.Label>Lookup Subscriber</S.Label>
                            <Field
                                name="subscriberLookup"
                                component={AsyncSelectField}
                                placeholder="Type name to search"
                                loadOptions={async (q: string) => {
                                    const results: SupportSubscriber[] = await claimEditorStore.searchSubscribersByName(q);
                                    return (results || []).map(r => ({
                                        label: [r.first, r.middle, r.last].filter(Boolean).join(' '),
                                        value: r.id as any,
                                        subscriber: r,
                                        info: formatLookupInfo(r.dob, r.sex ? _.startCase(_.lowerCase(r.sex)) : undefined),
                                    } as any));
                                }}
                                onChange={(opt: any, form: any) => {
                                    const sub = opt?.subscriber;
                                    if (!sub) {
                                        // Clearing the lookup unlinks the record, and that has
                                        // to be persisted — bailing out early left the previous
                                        // subscriber's id in the payload.
                                        setLink(null);
                                        debouncedSave();
                                        return;
                                    }
                                    const addr = sub?.addresses?.[0];
                                    // Demographics only. payerId, memberId and the patient
                                    // relationship describe the POLICY, not the person, so a
                                    // name lookup must never re-key which payer this subscriber
                                    // is billed under. Carrying them forward through `prev`
                                    // rather than re-listing them also keeps this immune to a
                                    // stale form bag.
                                    const demographics = {
                                        ssn: sub?.ssn || '',
                                        first: sub?.first || '',
                                        middle: sub?.middle || '',
                                        last: sub?.last || '',
                                        suffix: sub?.suffix || '',
                                        dob: sub?.dob || '',
                                        sex: (sub?.sex as any) || '',
                                        phone: sub?.phone || '',
                                        address1: addr?.lines?.[0] || '',
                                        address2: addr?.lines?.[1] || '',
                                        city: addr?.city || '',
                                        state: (addr?.state as any) || '',
                                        country: (addr?.country as any) || '',
                                        postal: addr?.postal || '',
                                    };

                                    // Key off the values actually written into the form, not off
                                    // the raw record — those are what a later edit is compared to.
                                    setLink(
                                        sub?.id
                                            ? { id: sub.id, key: subscriberIdentityKey(identityOf(demographics as any)) }
                                            : null,
                                    );

                                    form.setValues((prev: SubscriberPayerFormValues) => ({
                                        ...prev,
                                        ...demographics,
                                        subscriberLookup: opt.label,
                                    }));
                                    debouncedSave();
                                }}
                            />
                        </S.FormField>

                        <S.FormField span={2} id={fieldDomId(`${fieldPrefix}.payerId`)}>
                            <S.Label required>Lookup Payer</S.Label>
                            <Field
                                name="payerLookup"
                                component={AsyncSelectField}
                                // No `key` tied to arePayersLoaded: remounting when the preload lands
                                // wiped whatever the user typed while "Loading..." was shown. loadOptions
                                // awaits the preload itself, and flipping cacheOptions resets the cache.
                                cacheOptions={claimEditorStore.arePayersLoaded}
                                loadOptions={async (q: string) => {
                                    if (!claimEditorStore.arePayersLoaded) {
                                        await claimEditorStore.preloadPayers();
                                    }

                                    const list: ProviderPayer[] = (claimEditorStore.payers as ProviderPayer[]) || [];

                                    // Filtering AND ranking live in services/payer-search so they
                                    // can be unit tested — this used to be an untestable closure.
                                    //
                                    // Ranking matters as much as the cap: the list arrives NAME
                                    // ASCENDING, so truncating it alphabetically hid the payer
                                    // literally called "Medicare Part B" behind fifty payers whose
                                    // names merely contain the word (FE-34).
                                    const { matches, hidden } = searchPayers(list, q || '', MAX_PAYER_OPTIONS);

                                    const options = matches.map(
                                        p =>
                                            ({
                                                label: p.name || '',
                                                value: p.id as any,
                                                payer: p,
                                                info: p.coverage ? '| ' + p.coverage : undefined,
                                            } as any),
                                    );

                                    // A truncated list must SAY it is truncated. Capped at 10 with
                                    // no notice, "blue" returned ten BCBS plans ending at Florida
                                    // and every plan after F looked as though it did not exist —
                                    // on a claims console that is a mis-routed claim, not a
                                    // cosmetic issue.
                                    if (hidden > 0) {
                                        options.push({
                                            label: `+${hidden} more — keep typing to narrow`,
                                            value: '__truncated__',
                                            isDisabled: true,
                                        } as any);
                                    }

                                    return options;
                                }}
                                getDefault={async (id: string) => {
                                    if (!id) return null;
                                    try {
                                        const payer = await claimEditorStore.getPayerById(id);
                                        if (payer && claimEditorStore.isPayerIdActive(id)) {
                                            return {
                                                label: payer.name || '',
                                                value: payer.id as any,
                                                payer,
                                            } as any;
                                        }
                                        return {
                                            label: payer?.name
                                                ? `${payer.name} (disabled)`
                                                : DISABLED_PAYER_MESSAGE,
                                            value: id,
                                        } as any;
                                    } catch (e) {
                                        console.warn('payer getDefault failed', e);
                                    }
                                    // Keep the select interactive when the current payer
                                    // cannot be resolved (e.g. disabled on provider).
                                    return {
                                        label: DISABLED_PAYER_MESSAGE,
                                        value: id,
                                    } as any;
                                }}
                                onChange={(opt: any, form: any) => {
                                    const payer = opt?.payer;
                                    if (!payer) {
                                        // Clearing the select has to clear the id as well.
                                        // Bailing out here left `payerId` populated, so the
                                        // field read as empty while the submitted claim still
                                        // carried the previous payer. AsyncSelectField clears
                                        // `payerLookup` itself.
                                        form.setFieldValue('payerId', '');
                                        debouncedSave();
                                        return;
                                    }
                                    form.setFieldValue('payerLookup', String(payer.id || ''));
                                    form.setFieldValue('payerId', payer.id);
                                    // Avoid full setValues replacement here; it can clobber in-progress edits.
                                    debouncedSave();
                                }}
                            />
                        </S.FormField>

                        <S.FormField id={fieldDomId(`${fieldPrefix}.first`)}>
                            <S.Label required>Subscriber First Name</S.Label>
                            <Field name="first" placeholder="Subscriber First Name" component={InputField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Subscriber Middle Name</S.Label>
                            <Field name="middle" placeholder="Subscriber Middle Name" component={InputField} />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.last`)}>
                            <S.Label required>Subscriber Last Name</S.Label>
                            <Field name="last" placeholder="Subscriber Last Name" component={InputField} />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.suffix`)}>
                            <S.Label>Suffix</S.Label>
                            <Field name="suffix" placeholder="Suffix" component={InputField} maxLength={10 as any} />
                        </S.FormField>

                        <S.FormField id={fieldDomId(`${fieldPrefix}.dob`)}>
                            <S.Label required>Subscriber Date of Birth</S.Label>
                            <Field name="dob" placeholder="YYYY-MM-DD" component={DatePickerField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Subscriber Sex</S.Label>
                            <Field
                                name="sex"
                                component={SelectField}
                                options={sexOptions as any}
                                placeholder="Select"
                                isClearable={true}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Patient Relationship to Subscriber</S.Label>
                            <Field
                                name="relationshipToPatient"
                                component={SelectField}
                                options={relationshipOptions as any}
                                placeholder="Select"
                                isClearable={true}
                            />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.memberId`)}>
                            <S.Label required>Member ID</S.Label>
                            <Field name="memberId" placeholder="Member ID" component={InputField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>SSN</S.Label>
                            <Field name="ssn" placeholder="Social security number" component={InputField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Phone Number</S.Label>
                            <Field name="phone" placeholder="Phone Number" component={InputField} />
                        </S.FormField>

                        <S.FormField span={2} id={fieldDomId(`${fieldPrefix}.addresses[0].lines[0]`)}>
                            <S.Label required>Address Line 1</S.Label>
                            <Field name="address1" placeholder="Address Line 1" component={InputField} />
                        </S.FormField>
                        <S.FormField span={2}>
                            <S.Label>Address Line 2</S.Label>
                            <Field name="address2" placeholder="Address Line 2" component={InputField} />
                        </S.FormField>

                        <S.FormField id={fieldDomId(`${fieldPrefix}.addresses[0].city`)}>
                            <S.Label required>City</S.Label>
                            <Field name="city" placeholder="City" component={InputField} />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.addresses[0].state`)}>
                            <S.Label required>State</S.Label>
                            <Field
                                name="state"
                                component={SelectField}
                                options={stateOptions as any}
                                placeholder="Select State"
                                isClearable={false}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Country</S.Label>
                            <Field
                                name="country"
                                component={SelectField}
                                options={countryOptions as any}
                                placeholder="Select Country"
                                isClearable={true}
                            />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.addresses[0].postal`)}>
                            <S.Label required>Postal Code</S.Label>
                            <Field name="postal" placeholder="Postal Code" component={InputField} />
                        </S.FormField>
                    </S.FormGrid>
                </S.FormContainer>
                );
            }}
        </Formik>
    );
});

export default SubscriberPayerForm;


